import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/* Shared dashboard date-range filter. The TopBar selector writes it; the
   Overview page reads it so the KPI cards / payment split / alerts reflect the
   chosen period (previously the selector was cosmetic and the cards were always
   "today"). */

const DAY_MS = 86_400_000;

export type DateRangeLabel =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'This Month'
  | 'Custom';

export const DATE_RANGE_OPTIONS: DateRangeLabel[] = [
  'Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Custom',
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Resolve a non-custom label to a concrete [from, to) window (local time). */
export function rangeBounds(label: Exclude<DateRangeLabel, 'Custom'>, now: Date = new Date()): { from: Date; to: Date } {
  const today = startOfDay(now);
  switch (label) {
    case 'Today':       return { from: today, to: now };
    case 'Yesterday':   return { from: new Date(today.getTime() - DAY_MS), to: today };
    case 'Last 7 Days': return { from: new Date(today.getTime() - 6 * DAY_MS), to: now };
    case 'Last 30 Days':return { from: new Date(today.getTime() - 29 * DAY_MS), to: now };
    case 'This Month':  return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
  }
}

interface DateCtx {
  label: DateRangeLabel;
  setLabel: (l: DateRangeLabel) => void;
  /** yyyy-mm-dd strings for the Custom range pickers. */
  customFrom: string;
  customTo: string;
  setCustomFrom: (d: string) => void;
  setCustomTo: (d: string) => void;
  from: Date;
  to: Date;
}

const Ctx = createContext<DateCtx | null>(null);

export function DashboardDateProvider({ children }: { children: ReactNode }) {
  const [label, setLabel] = useState<DateRangeLabel>('Today');
  const todayStr = startOfDay(new Date()).toLocaleDateString('en-CA'); // yyyy-mm-dd
  const [customFrom, setCustomFrom] = useState<string>(todayStr);
  const [customTo, setCustomTo] = useState<string>(todayStr);

  const value = useMemo<DateCtx>(() => {
    let from: Date;
    let to: Date;
    if (label === 'Custom') {
      from = customFrom ? startOfDay(new Date(`${customFrom}T00:00:00`)) : startOfDay(new Date());
      // inclusive end day → up to the start of the day after customTo
      to = customTo ? new Date(startOfDay(new Date(`${customTo}T00:00:00`)).getTime() + DAY_MS) : new Date();
      if (to.getTime() < from.getTime()) to = new Date(from.getTime() + DAY_MS); // guard inverted range
    } else {
      ({ from, to } = rangeBounds(label));
    }
    return { label, setLabel, customFrom, customTo, setCustomFrom, setCustomTo, from, to };
  }, [label, customFrom, customTo]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDashboardDate(): DateCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDashboardDate must be used within DashboardDateProvider');
  return v;
}
